import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AddSharedAuthorityCommand,
    AuthorityRegisterSummaryDto,
    AuthorityTypeDto,
    DO_NOTHING,
    FEATURE_SITES,
    FlyFreelyError,
    FlyFreelyLoggingService,
    NameId,
    OrganisationAuthorityDto,
    OrganisationAuthorityService,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    OrganisationService,
    OrganisationSubscriptionDto,
    PersonDto,
    PersonsOrganisationDto,
    RemoveSharedAuthorityCommand,
    RpaTypeDto,
    RpaTypesService,
    ShareLicencesCommand,
    ShareResourceCommand,
    SimpleAuthorityDto,
    StartOrganisationRelationshipCommand,
    SubscriptionService,
    UnshareResourceCommand,
    UpdateSharedLicencesCommand,
    UserService,
    WorkTracker,
    hasAnyPermission,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import { FormatAuthorityPipe } from '@flyfreely-portal-ui/ui';
import { Angulartics2 } from 'angulartics2';
import { Subject, combineLatest, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthorityDialoguesService } from '../../../../authorities/src/lib/authority-dialogues.service';
import { toSimpleAuthority } from '../../../../authorities/src/lib/helpers';
import { OrganisationRelationshipsUiService } from './organisation-relationships-ui.service';

interface Authority extends NameId {
    isPrimary: boolean;
}

@Component({
    selector: 'shared-resources',
    templateUrl: './shared-resources.component.html',
    providers: [WorkTracker, OrganisationRelationshipsUiService]
})
export class SharedResources implements OnInit {
    @Input() organisation: PersonsOrganisationDto;

    person: PersonDto;

    newChild = new FormControl(undefined, [Validators.required]);
    addAuthority = new FormControl(undefined, [Validators.required]);
    addRpaType = new FormControl(undefined, [Validators.required]);
    editLicences: FormControl[] = [];

    children: OrganisationRelationshipDto[];
    additionalOrganisations: PersonsOrganisationDto[];
    allAuthorities: Authority[];
    addableAuthorities: Authority[] = [];
    availableRpaTypes: RpaTypeDto[] = [];
    addableRpaTypes: { [relationshipId: number]: RpaTypeDto[] } = {};

    currentSubscription: OrganisationSubscriptionDto;
    sharedSubscriptions: { [relationId: number]: OrganisationSubscriptionDto };
    editingLicence: number;
    availableLicences = 0;

    isReadOnlyAdmin = false;
    hasSites = false;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private organisationService: OrganisationService,
        private organisationRelationshipService: OrganisationRelationshipService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private userService: UserService,
        private subscriptionService: SubscriptionService,
        private commonDialoguesService: CommonDialoguesService,
        private formatAuthorityPipe: FormatAuthorityPipe,
        private organisationRelationshipUiService: OrganisationRelationshipsUiService,
        private logging: FlyFreelyLoggingService,
        private angulartics2: Angulartics2,
        private workTracker: WorkTracker,
        private rpaTypesService: RpaTypesService
    ) {}

    ngOnInit(): void {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.person = this.userService.getCurrentUser();
        this.refreshPermissions();

        combineLatest([
            this.organisationRelationshipUiService.subscription$,
            this.organisationRelationshipUiService.children$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([currentSubscription, children]) => {
                this.currentSubscription = currentSubscription;
                this.children = children;
                this.parseSharedSubscriptions();
                this.findOtherOrganisations();
                this.updateAddableRpaTypes();
            });

        merge(
            this.organisationRelationshipService.change$,
            this.rpaTypesService.change$
        )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.updateAddableRpaTypes();
            });

        this.organisationRelationshipUiService.setup(this.organisation.id);
        this.findAuthorities();
        this.findRpaTypes();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private updateAddableRpaTypes() {
        if (this.children == null || this.availableRpaTypes == null) {
            return;
        }
        this.addableRpaTypes = this.children.reduce((acc, relationship) => {
            const sharedIds = relationship.sharedRpaTypeList.map(t => t.id);
            acc[relationship.id] = this.availableRpaTypes.filter(
                t => !sharedIds.includes(t.id)
            );
            return acc;
        }, {} as { [relationshipId: number]: RpaTypeDto[] });
    }

    findRpaTypes() {
        this.rpaTypesService
            .findRpaTypes(this.organisation.id)
            .subscribe(rpaTypes => {
                // Filter out global types (only custom types where organisationId != null)
                this.availableRpaTypes = rpaTypes.filter(
                    t => t.organisationId != null
                );
                this.updateAddableRpaTypes();
            })
            .add(this.workTracker.createTracker());
    }

    shareRpaType(relationship: OrganisationRelationshipDto) {
        const cmd: ShareResourceCommand = {
            resourceId: this.addRpaType.value
        };
        this.organisationRelationshipService
            .shareRpaType(this.organisation.id, relationship.id, cmd)
            .subscribe({
                next: result => {
                    this.addRpaType.setValue(undefined);
                    this.angulartics2.eventTrack.next({
                        action: 'share-rpa-type',
                        properties: {
                            category: 'shared-resources',
                            label: result.id
                        }
                    });
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while sharing RPA Type: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    removeRpaType(
        relationship: OrganisationRelationshipDto,
        rpaType: RpaTypeDto
    ) {
        const cmd: UnshareResourceCommand = { resourceId: rpaType.id };
        this.organisationRelationshipService
            .unshareRpaType(this.organisation.id, relationship.id, cmd)
            .subscribe({
                next: result => {
                    this.angulartics2.eventTrack.next({
                        action: 'unshare-rpa-type',
                        properties: {
                            category: 'shared-resources',
                            label: result.id
                        }
                    });
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while removing RPA Type: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    refreshPermissions() {
        this.isReadOnlyAdmin =
            hasAnyPermission(
                this.organisation,
                PersonsOrganisationDto.Permissions.ORGANISATION_ADMIN_VIEW
            ) &&
            !hasAnyPermission(
                this.organisation,
                PersonsOrganisationDto.Permissions.ORGANISATION_EDIT
            );

        this.hasSites = hasFeatureFlag(this.organisation, FEATURE_SITES);
    }

    parseSharedSubscriptions() {
        this.editLicences = [];
        const findMin = (sub: OrganisationSubscriptionDto) => {
            return sub != null && sub.limits[0] != null
                ? sub.limits[0].instances -
                      (sub.limits[0].availableLicenceCount ?? 0)
                : 0;
        };
        const findMax = (sub: OrganisationSubscriptionDto) => {
            return this.currentSubscription != null &&
                this.currentSubscription?.limits[0] != null &&
                this.currentSubscription?.limits[0].availableLicenceCount !=
                    null &&
                sub != null &&
                sub.limits[0] != null
                ? (sub.limits[0].instances ?? 0) +
                      (this.currentSubscription.limits[0]
                          .availableLicenceCount ?? 0)
                : this.currentSubscription?.limits[0]?.availableLicenceCount !=
                  null
                ? this.currentSubscription?.limits[0]?.availableLicenceCount
                : 0;
        };
        if (this.children.length > 0) {
            this.sharedSubscriptions = this.children.reduce(
                (acc, s) => ({ ...acc, [s.id]: s.sharedSubscription }),
                {}
            );
            this.children.forEach(c =>
                this.editLicences.push(
                    new FormControl(
                        undefined,
                        Validators.compose([
                            Validators.min(
                                findMin(this.sharedSubscriptions[c.id])
                            ),
                            Validators.max(
                                findMax(this.sharedSubscriptions[c.id])
                            )
                        ])
                    )
                )
            );
        } else {
            this.sharedSubscriptions = null;
        }
    }

    showAuthority(authorityType: AuthorityTypeDto, authority: any) {
        this.organisationAuthorityService
            .findAuthority(authority.id, this.organisation.id)
            .subscribe({
                next: auth => {
                    const modal =
                        this.authorityDialoguesService.showAuthorityDetails(
                            this.organisation.id,
                            authorityType,
                            auth
                        );
                    modal.content.onShowRegister
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(payload => {
                            this.showRegister(
                                <AuthorityTypeDto>payload.authorityType,
                                payload.authority,
                                payload.register
                            );
                        });
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading authority details: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    showRegister(
        authorityType: AuthorityTypeDto,
        authority: OrganisationAuthorityDto,
        register: AuthorityRegisterSummaryDto
    ) {
        if (
            register.registerEntityType ===
            AuthorityRegisterSummaryDto.RegisterEntityType.RPA
        ) {
            this.authorityDialoguesService.showRpaRegisterDialogue(
                this.organisation.id,
                authorityType,
                authority,
                register,
                this.organisation.id
            );
        } else {
            this.authorityDialoguesService.showPersonnelRegisterDialogue(
                this.organisation.id,
                authorityType,
                authority,
                register,
                this.organisation.id
            );
        }
    }

    // finds options for adding new child organisations
    findOtherOrganisations() {
        const childrenIds = this.children.map(
            child => child.childOrganisation.id
        );

        this.organisationService
            .findOrganisationsForPerson(this.person.id)
            .subscribe(orgs => {
                this.additionalOrganisations = orgs
                    .filter(
                        org =>
                            org.permissions.indexOf(
                                PersonsOrganisationDto.Permissions
                                    .ORGANISATION_EDIT
                            ) !== -1
                    )
                    .filter(org => {
                        return (
                            !childrenIds.includes(org.id) &&
                            org.id !== this.organisation.id &&
                            !org.personalOrganisation
                        );
                    });
            })
            .add(this.workTracker.createTracker());
    }

    addChildOrganisation() {
        const doneWorking = this.workTracker.createTracker();
        const cmd: StartOrganisationRelationshipCommand = {
            childOrganisationId: this.newChild.value
        };
        this.organisationRelationshipService
            .addChildRelationship(this.organisation.id, cmd)
            .toPromise()
            .then(results => {
                this.newChild.setValue(undefined);

                this.angulartics2.eventTrack.next({
                    action: 'add-child-organisation',
                    properties: {
                        category: 'shared-resources',
                        label: results.id
                    }
                });
                doneWorking();
            });
    }

    shareAuthority(relationship: OrganisationRelationshipDto) {
        const doneWorking = this.workTracker.createTracker();
        const cmd: AddSharedAuthorityCommand = {
            authorityId: this.addAuthority.value
        };
        this.organisationRelationshipService
            .shareAuthorityWithChild(this.organisation.id, relationship.id, cmd)
            .toPromise()
            .then(results => {
                this.addAuthority.setValue(undefined);
                this.angulartics2.eventTrack.next({
                    action: 'share-authority',
                    properties: {
                        category: 'shared-resources',
                        label: results.id
                    }
                });
                doneWorking();
            });
    }

    /**
     * Change licence sharing into edit mode
     */
    editRelationLicences(relationship: OrganisationRelationshipDto, i: number) {
        this.editLicences[i].setValue(
            relationship.sharedSubscription.limits[0].instances
        );
        this.editLicences[i].markAsPristine();
        this.editingLicence = relationship.id;
    }

    shareLicences(relationship: OrganisationRelationshipDto, i: number) {
        const command: ShareLicencesCommand = {
            licencedEntityType:
                relationship.sharedSubscription?.limits[0]
                    ?.licencedEntityType ??
                this.currentSubscription.limits[0].licencedEntityType,
            numberOfLicences:
                relationship.sharedSubscription != null
                    ? this.editLicences[i].value -
                      relationship.sharedSubscription?.limits[0]?.instances
                    : this.editLicences[i].value,
            subscriptionId: this.currentSubscription.id
        };
        this.subscriptionService
            .shareLicences(this.organisation.id, relationship.id, command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.logging.success(
                        `Updated shared licences for ${relationship.childOrganisation.name}`
                    );
                    this.editingLicence = null;
                    this.angulartics2.eventTrack.next({
                        action: relationship.sharedSubscription
                            ? 'update-shared-licence-count'
                            : 'share-subscription',
                        properties: {
                            category: 'shared-resources',
                            label: result.id,
                            value: result.sharedSubscription.limits[0].instances
                        }
                    });
                    this.editLicences[i].setValue(undefined);
                    this.editLicences[i].markAsPristine();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error sharing licences: ${error.message}`
                    );
                }
            });
    }

    updateSharedLicences(relationship: OrganisationRelationshipDto, i: number) {
        const command: UpdateSharedLicencesCommand = {
            numberOfLicences: this.editLicences[i].value
        };
        this.subscriptionService
            .updateSharedLicences(
                this.organisation.id,
                relationship.id,
                command
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.logging.success(
                        `Updated shared licences for ${relationship.childOrganisation.name}`
                    );
                    this.editingLicence = null;
                    this.angulartics2.eventTrack.next({
                        action: 'update-shared-licence-count',
                        properties: {
                            category: 'shared-resources',
                            label: result.id,
                            value: result.sharedSubscription.limits[0].instances
                        }
                    });
                    this.editLicences[i].setValue(undefined);
                    this.editLicences[i].markAsPristine();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error sharing licences: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    stopSharingLicences(relationship: OrganisationRelationshipDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Stop sharing subscription',
                `By continuing you will stop sharing this organisation's current subscription with ${relationship.childOrganisation.name} and remove all shared licences, regardless of whether they have been assigned to any of ${relationship.childOrganisation.name}'s personnel. Are you sure you wish to stop sharing your subscription?`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() =>
                this.subscriptionService
                    .unshareLicences(this.organisation.id, relationship.id)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: result => {
                            this.logging.success(
                                `Stopped sharing subscription and licences with ${relationship.childOrganisation.name}`
                            );
                            this.angulartics2.eventTrack.next({
                                action: 'unshare-subscription',
                                properties: {
                                    category: 'shared-resources',
                                    label: result.id
                                }
                            });
                        },
                        error: (error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error stopping subscription sharing: ${error.message}`
                            );
                        }
                    })
                    .add(this.workTracker.createTracker())
            )
            .catch(DO_NOTHING);
    }

    // Gets list of available authorities for the current user
    findAuthorities() {
        this.organisationAuthorityService
            .findAuthorities(this.organisation.id, this.organisation.id)
            .subscribe(authorities =>
                (this.allAuthorities = authorities
                    .filter(group => group.hasWorkflow)
                    .map(group =>
                        group.authorities.map(auth => ({
                            id: auth.id,
                            name: this.formatAuthorityPipe.transform(
                                toSimpleAuthority(
                                    auth,
                                    this.organisation,
                                    group
                                )
                            ),
                            isPrimary: group.isPrimary
                        }))
                    )
                    .reduce((acc, auths) => [...acc, ...auths], [])).sort(
                    (a, b) => a.name.localeCompare(b.name)
                )
            );
    }

    findAddableAuthorities(relationship: OrganisationRelationshipDto) {
        const added = relationship.sharedAuthorityList.map(auth => auth.id);
        this.addableAuthorities = this.allAuthorities.filter(
            auth => !added.includes(auth.id)
        );
    }

    findMinLicenceCount(relation: OrganisationRelationshipDto) {
        if (relation?.sharedSubscription?.limits?.length > 0) {
            return (
                relation.sharedSubscription.limits[0].instances -
                relation.sharedSubscription.limits[0].availableLicenceCount
            );
        } else {
            return 0;
        }
    }

    findMaxLicenceCount(relation: OrganisationRelationshipDto) {
        if (
            this.currentSubscription?.limits == null ||
            this.currentSubscription?.limits?.length === 0
        ) {
            return null;
        } else if (
            this.currentSubscription?.limits[0]?.availableLicenceCount !=
                null &&
            relation.sharedSubscription?.limits?.length > 0
        ) {
            return (
                relation.sharedSubscription.limits[0].instances +
                this.currentSubscription.limits[0].availableLicenceCount
            );
        } else {
            return this.currentSubscription.limits[0].availableLicenceCount;
        }
    }

    removeAuthority(
        relationship: OrganisationRelationshipDto,
        authority: SimpleAuthorityDto
    ) {
        const cmd: RemoveSharedAuthorityCommand = { authorityId: authority.id };
        this.organisationRelationshipService
            .unshareAuthority(this.organisation.id, relationship.id, cmd)
            .subscribe(result => {
                this.angulartics2.eventTrack.next({
                    action: 'unshare-authority',
                    properties: {
                        category: 'shared-resources',
                        label: result.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    removeOrganisation(relationship: OrganisationRelationshipDto) {
        this.organisationRelationshipService
            .removeChildRelationship(this.organisation.id, relationship.id)
            .subscribe(result => {
                this.angulartics2.eventTrack.next({
                    action: 'remove-child-organisation',
                    properties: {
                        category: 'shared-resources',
                        label: result.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }
}
